import { useState } from "react";
import { PhoneCall, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { getLeadTracking, trackLeadDeduped } from "../lib/fbpixel";
import { trackGaLead } from "../lib/ga";
import { trackFormStart } from "../lib/formTracking";
import ConsentField from "./ConsentField";

export default function CallbackWidget({ service, leadType = "general" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const tracking = getLeadTracking();
      await api.post("/leads", {
        name: form.name,
        phone: form.phone,
        message: `Callback request — ${service}`,
        lead_type: leadType,
        source_page: `callback-${service}`,
        marketing_consent: marketing,
        ...tracking,
      });
      trackLeadDeduped(tracking.event_id, { content_category: leadType, source_page: `callback-${service}` });
      trackGaLead({ lead_type: leadType, source_page: `callback-${service}` });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-[#151515] border border-[#D8D6D0]/20 px-4 py-3 text-sm text-[#F5F3EF] placeholder:text-[#D8D6D0]/40 focus:outline-none focus:border-[#F5F3EF]/70 transition-colors";

  return (
    <div className="fixed bottom-24 lg:bottom-8 right-4 lg:right-8 z-40">
      {open && (
        <div data-testid="callback-panel" className="mb-3 w-[320px] bg-[#151515] border border-[#D8D6D0]/20 shadow-2xl p-6">
          {sent ? (
            <div data-testid="callback-success" className="text-center py-4">
              <div className="font-serif-display text-2xl text-[#F5F3EF]">We're on it.</div>
              <p className="text-sm text-[#D8D6D0]/70 mt-2">A NorthCrest team member will call you back shortly during business hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="font-serif-display text-xl text-[#F5F3EF]">Request a callback</div>
              <p className="text-xs text-[#D8D6D0]/60">Leave your number — we'll call you back about {service.replace(/-/g, " ")}.</p>
              <input
                data-testid="callback-name"
                required
                placeholder="Name"
                className={input}
                value={form.name}
                onFocus={() => trackFormStart(`callback-${service}`)}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                data-testid="callback-phone"
                required
                minLength={7}
                pattern="[0-9\s\(\)\+\.\-]{7,}"
                title="Please enter a valid phone number"
                placeholder="Phone"
                className={input}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <ConsentField checked={marketing} onChange={setMarketing} testId="callback-consent" />
              <button
                type="submit"
                disabled={loading}
                data-testid="callback-submit"
                className="w-full px-6 py-3.5 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-xs uppercase tracking-widest transition-colors disabled:opacity-50"
              >
                {loading ? "Sending…" : "Call Me Back"}
              </button>
            </form>
          )}
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        data-testid="callback-toggle"
        aria-label={open ? "Close callback form" : "Request a callback"}
        className="ml-auto flex items-center gap-2 px-5 py-3.5 bg-[#0B2B1F] hover:bg-[#8C1D30] text-[#F5F3EF] text-xs uppercase tracking-widest shadow-2xl border border-[#D8D6D0]/15 transition-colors"
      >
        {open ? <X className="w-4 h-4" /> : <PhoneCall className="w-4 h-4" />}
        {open ? "Close" : "Call Me Back"}
      </button>
    </div>
  );
}
